import React from 'react'
import PropTypes from 'prop-types'
import { getCompanyName } from '../../context/AppConfigContext'
import i18n from 'i18next'

function InvoiceTemplate({ order }) {
  // Safely parse JSON fields
  let totalOrder = 0, totalItems = 0, totalVat = 0
  let products = [], loans = [], services = []

  try {
    const totals = JSON.parse(order.totals || '{}')
    totalOrder = totals.totalOrder || 0
    totalItems = totals.totalItems || 0
    totalVat = totals.totalVat || 0
  } catch {}

  try { products = JSON.parse(order.products || '[]') } catch {}
  try { loans = JSON.parse(order.loans || '[]') } catch {}
  try { services = JSON.parse(order.services || '[]') } catch {}

  // Get company name from config
  const companyName = getCompanyName()

  // Get current language
  const lang = i18n.language || 'en'

  // Translation strings
  const translations = {
    en: {
      orderDetails: 'Order Details',
      offerDetails: 'Offer Details',
      customerDetails: 'Customer Details',
      offerCustomerDetails: 'Offer Details',
      orderNumber: 'Order No.',
      offerNumber: 'Offer No.',
      customer: 'Customer',
      date: 'Date',
      deliveryDate: 'Delivery Date',
      status: 'Status',
      product: 'Product',
      quantity: 'Qty',
      unitPrice: 'Unit Price',
      total: 'Total',
      vat: 'VAT',
      subtotal: 'Subtotal',
      loans: 'Loans',
      services: 'Services',
      totalItems: 'Total Items',
      totalVat: 'Total VAT',
      grandTotal: 'Grand Total',
      printedOn: 'Printed on',
      page: 'Page',
    },
    gr: {
      orderDetails: 'Στοιχεία Παραγγελίας',
      offerDetails: 'Στοιχεία Προσφοράς',
      customerDetails: 'Στοιχεία Πελάτη',
      offerCustomerDetails: 'Στοιχεία Προσφοράς',
      orderNumber: 'Αρ. Παραγγελίας',
      offerNumber: 'Αρ. Προσφοράς',
      customer: 'Πελάτης',
      date: 'Ημερομηνία',
      deliveryDate: 'Ημ. Παράδοσης',
      status: 'Κατάσταση',
      product: 'Προϊόν',
      quantity: 'Ποσ.',
      unitPrice: 'Τιμή Μον.',
      total: 'Σύνολο',
      vat: 'ΦΠΑ',
      subtotal: 'Μερικό Σύνολο',
      loans: 'Δάνεια',
      services: 'Υπηρεσίες',
      totalItems: 'Σύνολο Τεμαχίων',
      totalVat: 'Σύνολο ΦΠΑ',
      grandTotal: 'Γενικό Σύνολο',
      printedOn: 'Εκτυπώθηκε στις',
      page: 'Σελίδα',
    },
    de: {
      orderDetails: 'Bestelldetails',
      offerDetails: 'Angebotsdetails',
      customerDetails: 'Kundendetails',
      offerCustomerDetails: 'Angebotsdetails',
      orderNumber: 'Bestellnr.',
      offerNumber: 'Angebotsnr.',
      customer: 'Kunde',
      date: 'Datum',
      deliveryDate: 'Lieferdatum',
      status: 'Status',
      product: 'Produkt',
      quantity: 'Menge',
      unitPrice: 'Stückpreis',
      total: 'Gesamt',
      vat: 'MwSt.',
      subtotal: 'Zwischensumme',
      loans: 'Darlehen',
      services: 'Dienstleistungen',
      totalItems: 'Gesamtartikel',
      totalVat: 'MwSt. Gesamt',
      grandTotal: 'Gesamtbetrag',
      printedOn: 'Gedruckt am',
      page: 'Seite',
    }
  }

  const t = translations[lang] || translations.en
  const isOffer = order.status === 'OFFER'
  const today = new Date().toLocaleDateString(lang === 'gr' ? 'el-GR' : lang === 'de' ? 'de-DE' : 'en-GB')

  // Parse delivery date
  let deliveryDate = ''
  if (order.expected_delivery_date) {
    try {
      deliveryDate = new Date(order.expected_delivery_date).toLocaleDateString(lang === 'gr' ? 'el-GR' : lang === 'de' ? 'de-DE' : 'en-GB')
    } catch {}
  }

  const styles = `
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body {
      font-family: 'Segoe UI', Arial, sans-serif;
      font-size: 11px;
      color: #333;
      line-height: 1.4;
      padding: 20px;
    }
    .invoice-container {
      max-width: 800px;
      margin: 0 auto;
    }
    .header {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      padding-bottom: 20px;
      border-bottom: 3px solid #3b82f6;
      margin-bottom: 20px;
    }
    .company-info {
      text-align: right;
    }
    .company-name {
      font-size: 24px;
      font-weight: 700;
      color: #1e40af;
      margin-bottom: 4px;
    }
    .company-tagline {
      font-size: 11px;
      color: #6b7280;
    }
    .customer-info h2 {
      font-size: 14px;
      color: #374151;
      margin-bottom: 12px;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }
    .info-grid {
      display: grid;
      grid-template-columns: auto 1fr;
      gap: 4px 12px;
    }
    .info-label {
      font-weight: 600;
      color: #6b7280;
    }
    .info-value {
      color: #111827;
    }
    .order-badge {
      display: inline-block;
      background: #3b82f6;
      color: white;
      padding: 4px 12px;
      border-radius: 4px;
      font-weight: 600;
      font-size: 14px;
      margin-bottom: 8px;
    }
    .offer-badge {
      background: #f59e0b;
    }
    .section-title {
      font-size: 12px;
      font-weight: 600;
      color: #374151;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      margin: 20px 0 10px;
      padding-bottom: 6px;
      border-bottom: 1px solid #e5e7eb;
    }
    table {
      width: 100%;
      border-collapse: collapse;
      margin-bottom: 15px;
    }
    th {
      background: #f8fafc;
      padding: 10px 8px;
      text-align: left;
      font-weight: 600;
      font-size: 10px;
      color: #6b7280;
      text-transform: uppercase;
      border-bottom: 2px solid #e5e7eb;
    }
    td {
      padding: 10px 8px;
      border-bottom: 1px solid #f3f4f6;
      vertical-align: top;
    }
    tr:hover { background: #fafafa; }
    .text-right { text-align: right; }
    .text-center { text-align: center; }
    .product-name {
      font-weight: 500;
      color: #111827;
    }
    .category-row td {
      background: #f0f9ff;
      font-weight: 600;
      color: #0369a1;
      text-align: center;
      padding: 8px;
    }
    .totals-section {
      margin-top: 20px;
      display: flex;
      justify-content: flex-end;
    }
    .totals-box {
      width: 280px;
      border: 1px solid #e5e7eb;
      border-radius: 8px;
      overflow: hidden;
    }
    .totals-row {
      display: flex;
      justify-content: space-between;
      padding: 10px 16px;
      border-bottom: 1px solid #f3f4f6;
    }
    .totals-row:last-child { border-bottom: none; }
    .totals-label {
      color: #6b7280;
    }
    .totals-value {
      font-weight: 600;
      color: #111827;
    }
    .grand-total {
      background: #3b82f6;
      color: white;
    }
    .grand-total .totals-label,
    .grand-total .totals-value {
      color: white;
      font-size: 14px;
    }
    .footer {
      margin-top: 30px;
      padding-top: 15px;
      border-top: 1px solid #e5e7eb;
      display: flex;
      justify-content: space-between;
      font-size: 10px;
      color: #9ca3af;
    }
    @media print {
      body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    }
  `

  return (
    <div>
      <style dangerouslySetInnerHTML={{ __html: styles }} />
      <div className="invoice-container">
        <div className="header">
          <div className="customer-info">
            <div className={`order-badge ${isOffer ? 'offer-badge' : ''}`}>
              {isOffer ? t.offerNumber : t.orderNumber} #{order.id}
            </div>
            <h2>{isOffer ? t.offerCustomerDetails : t.customerDetails}</h2>
            <div className="info-grid">
              <span className="info-label">{t.customer}:</span>
              <span className="info-value">{order.name}</span>
              {deliveryDate && (
                <>
                  <span className="info-label">{t.deliveryDate}:</span>
                  <span className="info-value">{deliveryDate}</span>
                </>
              )}
              <span className="info-label">{t.status}:</span>
              <span className="info-value">{order.status}</span>
            </div>
          </div>
          <div className="company-info">
            <div className="company-name">{companyName}</div>
            <div className="company-tagline">{t.date}: {today}</div>
          </div>
        </div>

        {/* Products Table */}
        {products.length > 0 && (
          <>
            <div className="section-title">{isOffer ? t.offerDetails : t.orderDetails}</div>
            <table>
              <thead>
                <tr>
                  <th style={{ width: '40%' }}>{t.product}</th>
                  <th className="text-center" style={{ width: '10%' }}>{t.quantity}</th>
                  <th className="text-right" style={{ width: '15%' }}>{t.unitPrice}</th>
                  <th className="text-right" style={{ width: '15%' }}>{t.subtotal}</th>
                  <th className="text-right" style={{ width: '10%' }}>{t.vat}</th>
                  <th className="text-right" style={{ width: '10%' }}>{t.total}</th>
                </tr>
              </thead>
              <tbody>
                {products.map((product, index) => {
                  const subtotal = Number(product.totalPrdAmmount || 0)
                  const vatAmount = Number(product.vatProducts || 0)
                  const total = subtotal + vatAmount
                  return (
                    <tr key={index}>
                      <td className="product-name">{product.name}</td>
                      <td className="text-center">{product.quantity}</td>
                      <td className="text-right">€{Number(product.salesprice || 0).toFixed(2)}</td>
                      <td className="text-right">€{subtotal.toFixed(2)}</td>
                      <td className="text-right">€{vatAmount.toFixed(2)}</td>
                      <td className="text-right">€{total.toFixed(2)}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </>
        )}

        {/* Loans Table */}
        {loans.length > 0 && (
          <>
            <div className="section-title">{t.loans}</div>
            <table>
              <thead>
                <tr>
                  <th style={{ width: '60%' }}>{t.product}</th>
                  <th className="text-center" style={{ width: '20%' }}>{t.quantity}</th>
                  <th className="text-right" style={{ width: '20%' }}>{t.unitPrice}</th>
                </tr>
              </thead>
              <tbody>
                {loans.map((loan, index) => (
                  <tr key={index}>
                    <td className="product-name">{loan.name}</td>
                    <td className="text-center">{loan.quantity}</td>
                    <td className="text-right">€{Number(loan.salesprice || 0).toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}

        {/* Services Table */}
        {services.length > 0 && (
          <>
            <div className="section-title">{t.services}</div>
            <table>
              <thead>
                <tr>
                  <th style={{ width: '60%' }}>{t.product}</th>
                  <th className="text-center" style={{ width: '20%' }}>{t.quantity}</th>
                  <th className="text-right" style={{ width: '20%' }}>{t.unitPrice}</th>
                </tr>
              </thead>
              <tbody>
                {services.map((service, index) => (
                  <tr key={index}>
                    <td className="product-name">{service.name}</td>
                    <td className="text-center">{service.quantity}</td>
                    <td className="text-right">€{Number(service.salesprice || 0).toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}

        {/* Totals */}
        <div className="totals-section">
          <div className="totals-box">
            <div className="totals-row">
              <span className="totals-label">{t.totalItems}</span>
              <span className="totals-value">{totalItems}</span>
            </div>
            <div className="totals-row">
              <span className="totals-label">{t.totalVat}</span>
              <span className="totals-value">€{Number(totalVat || 0).toFixed(2)}</span>
            </div>
            <div className="totals-row grand-total">
              <span className="totals-label">{t.grandTotal}</span>
              <span className="totals-value">€{Number(totalOrder || 0).toFixed(2)}</span>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="footer">
          <span>{companyName}</span>
          <span>{t.printedOn} {today}</span>
        </div>
      </div>
    </div>
  )
}

InvoiceTemplate.propTypes = {
  order: PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    status: PropTypes.string.isRequired,
    expected_delivery_date: PropTypes.string,
    products: PropTypes.string,
    loans: PropTypes.string,
    services: PropTypes.string,
    totals: PropTypes.string,
    shipping: PropTypes.string,
  }).isRequired,
}

export default InvoiceTemplate
